import { Injectable, inject } from '@angular/core';
import type { Stripe } from 'stripe';

import { HttpClient } from '@angular/common/http';
import { CustomerBillingPermission } from '@picflow/types';
import { lastValueFrom } from 'rxjs';
import { GbForStoragePrice, msInADay, productConfig, trueUpDurationInDays } from '../consts';
import { PICFLOW_ANALYTICS } from '../picflow-analytics';
import { PICFLOW_CONFIG } from '../picflow-config';
import { PlanState } from '../types/plan-state.type';
import { Discount, Plan, PlanPrice, PlanProduct, StorageAddon } from '../types/plan.type';
import { DataLoaderService } from './data-loader.service';
import { LoggedInAnalyticsService } from './loggedin-analytics.service';
import { PromotionService } from './promotion.service';
import { StateService } from './state.service';

@Injectable({
  providedIn: 'root',
})
export class PlanService {
  private analytics = inject(PICFLOW_ANALYTICS);
  private loggedInAnalytics = inject(LoggedInAnalyticsService);
  private config = inject(PICFLOW_CONFIG);
  private http = inject(HttpClient);
  private loader = inject(DataLoaderService);
  private state = inject(StateService);
  private promotionService = inject(PromotionService);

  private findPlanPriceItem(items: Stripe.SubscriptionItem[]): Stripe.SubscriptionItem {
    const item = items.find(i => {
      const lookupKey: string = i.price.lookup_key;
      if (lookupKey.startsWith('pro_')) return true;
      if (lookupKey.startsWith('team_')) return true;
      if (lookupKey.startsWith('plus_')) return true;
      if (lookupKey.startsWith('basic_')) return true;
      if (lookupKey.startsWith('free_')) return true;
      if (lookupKey.startsWith('enterprise_')) return true;
      return false;
    });
    if (!item)
      setTimeout(() => {
        throw new Error('Could not find plan price');
      });
    return item || items[0];
  }

  private async getPlanPromo(stripeSub: Stripe.Subscription) {
    const promoCode = stripeSub.discount?.promotion_code as Stripe.PromotionCode;
    const currentPromoCode = promoCode?.code;
    if (currentPromoCode) {
      return await this.promotionService.checkCode(promoCode?.code);
    }

    if (stripeSub.discount) {
      // This is needed for when a promotion doesn't have a code
      return this.promotionService.convertPromo(stripeSub.discount as unknown as Stripe.PromotionCode);
    }
  }

  private async getSubscription() {
    const stripeSub = await lastValueFrom(
      this.http.get<Stripe.Subscription>(`${this.config.apiEndpoint}/billing/subscriptions`)
    );

    return {
      ...stripeSub,
      promo: await this.getPlanPromo(stripeSub),
    };
  }

  public async getPlan(): Promise<Plan> {
    this.loader.removeFuzzy('billingCustomer');
    this.loader.remove('plan-name_');
    this.loader.removeFuzzy('galleries');
    this.loader.removeFuzzy('plan-name_');

    if (!this.state.activeTenant.id_stripe_subscription) {
      const storage = productConfig['Picflow Free'].core_storage;
      const plan: Plan = {
        latestInvoicePeriodEnd: undefined,
        status: 'active',
        subscriptionId: undefined,
        tenantId: this.state.activeTenant.id,
        product: {
          id: 'free',
          isFree: true,
          isMax: false,
          name: 'Free',
        },
        storage: {
          total: storage,
          plan: storage,
          addOn: 0n,
        },
        scopes: productConfig['Picflow Free'].scopes,
        maxTenantSeats: productConfig['Picflow Free'].max_tenant_seats,
        price: <PlanPrice>{ amount: 0, billingFrequency: 'yearly' },
        willRenew: true,
        billingPeriodEnd: undefined,
        total: 0,
        subTotal: 0,
        storageAddon: undefined,
        discount: undefined,
        isEnterprise: false,
        mainPriceQuantity: undefined,
        hostedInvoiceUrl: undefined,
      };

      this.state.plan = plan;
      if (this.state.user) this.loggedInAnalytics.identify();
      return plan;
    }

    const stripeSub = await this.getSubscription();

    const planPriceItem = this.findPlanPriceItem(stripeSub.items.data);
    const planPrice = planPriceItem.price;
    const planProduct = planPrice.product as Stripe.Product;

    const price: PlanPrice = {
      id: planPrice.id,
      lookupKey: planPrice.lookup_key,
      amount: planPrice.unit_amount / 100,
      billingFrequency: planPrice.recurring.interval === 'month' ? 'monthly' : 'yearly',
    };

    let name = planProduct.name;
    if (name.includes('Picflow ')) name = name.split('Picflow ')[1];

    const product: PlanProduct = {
      id: planProduct.id,
      isFree: planProduct.name === 'Picflow Free',
      isMax: ['Picflow Pro', 'Picflow Team', 'Picflow Enterprise'].includes(planProduct.name),
      name,
    };

    const _storageAddon = stripeSub.items.data.find(p => p.price.lookup_key.includes('storage'));
    let storageAddon: StorageAddon;

    if (_storageAddon) {
      const storageAddonProduct = _storageAddon.price.product as Stripe.Product;
      storageAddon = {
        id: _storageAddon.price.id,
        quantity: _storageAddon.quantity,
        unitCost: _storageAddon.price.unit_amount,
        productId: storageAddonProduct.id,
      };
    }

    let total = stripeSub.items.data.reduce((t, item) => t + item.price.unit_amount * item.quantity, 0);
    const subTotal = total;
    let discount: Discount;

    if (stripeSub.discount && stripeSub.promo) {
      discount = {
        start: stripeSub.discount.start * 1000,
        end: stripeSub.discount.end * 1000,
        promo: stripeSub.promo,
      };
      const discountAmount = discount.promo?.amountOff || (price.amount / 100) * discount.promo.percentOff;
      if (this.promotionService.appliesTo(discount.promo, product)) {
        total -= discountAmount * 100;
      }
    }

    const isEnterprise = price.lookupKey.startsWith('enterprise_');

    const latestInvoice = stripeSub.latest_invoice as Stripe.Invoice;

    const config = productConfig[`Picflow ${product.name}`];
    const planStorageTotal = config.core_storage;
    const storageAddonTotal = _storageAddon
      ? BigInt(GbForStoragePrice(_storageAddon.price) * _storageAddon.quantity) *
        BigInt(1024) *
        BigInt(1024) *
        BigInt(1024)
      : 0n;

    const plan: Plan = {
      latestInvoicePeriodEnd: latestInvoice?.period_end * 1000,
      status: stripeSub.status,
      subscriptionId: stripeSub.id,
      tenantId: this.state.activeTenant.id,
      product,
      storage: {
        total: planStorageTotal + storageAddonTotal,
        plan: planStorageTotal,
        addOn: storageAddonTotal,
      },
      scopes: config.scopes,
      maxTenantSeats: config.max_tenant_seats,
      price,
      willRenew: !stripeSub.cancel_at_period_end,
      billingPeriodEnd: stripeSub.current_period_end * 1000,
      total,
      subTotal,
      storageAddon,
      trialEnd: stripeSub.trial_end * 1000,
      discount,
      // previousPromoCode: stripeSub.previousPromoCode,
      isEnterprise,
      mainPriceQuantity: planPriceItem.quantity,
      hostedInvoiceUrl: latestInvoice?.hosted_invoice_url,
    };

    if (isEnterprise) {
      plan.nextTrueUpDate = this.getNextTrueUpPeriodEndDate(plan.billingPeriodEnd);
      const daysRemainingUntilTrueUpEnd = (plan.nextTrueUpDate - Date.now()) / msInADay;
      plan.inTrueUp = daysRemainingUntilTrueUpEnd < trueUpDurationInDays;
    }

    this.state.plan = plan;
    if (this.state.user) this.loggedInAnalytics.identify();

    return plan;
  }

  public async getBillingCustomer(includeTaxId = false) {
    const key = 'billingCustomer' + (includeTaxId ? '_with-tax-id' : '');
    const customer = await this.loader.get(key, () =>
      lastValueFrom(
        this.http.get<{
          customer: Stripe.Customer;
          billing_permissions: CustomerBillingPermission;
        }>(`${this.config.apiEndpoint}/billing/customer${includeTaxId ? '?expand[]=tax_ids' : ''}`)
      )
    );
    return customer;
  }

  public async getBalance() {
    const customer = await this.getBillingCustomer();
    return -customer.customer.balance;
  }

  /**
   * Returns the next true up period end date based on the billing period end date
   */
  private getNextTrueUpPeriodEndDate(billingPeriodEndMs: number): number {
    const billingPeriodEndDate = new Date(billingPeriodEndMs);
    // removing three months from the billing period end date
    const monthsBetweenTrueUps = 3;
    const threeMonthsBefore = new Date(billingPeriodEndDate);
    threeMonthsBefore.setMonth(threeMonthsBefore.getMonth() - monthsBetweenTrueUps);
    // if the date is in the past, we return the billing period end date
    const dateIsInThePast = threeMonthsBefore.getTime() < Date.now();
    if (dateIsInThePast) return billingPeriodEndMs;
    // otherwise we recursively call this function with the new date
    return this.getNextTrueUpPeriodEndDate(threeMonthsBefore.getTime());
  }

  public getPlanState(): PlanState {
    if (this.state.plan.isEnterprise) return 'active';
    if (this.state.plan.price.amount === 0) return 'free';
    if (this.state.plan.status === 'past_due' && !this.state.temporarilyHideUpgradeCta) return 'past-due';
    if (this.state.plan.status === 'trialing') {
      if (this.state.plan.willRenew) return 'trialing-will-renew';
      return 'trialing-wont-renew';
    }
    if (this.state.plan.willRenew) return 'active';
    return 'pending-termination';
  }
}
